import {
  AfterViewInit,
  Component,
  ComponentRef,
  ViewChild,
  ViewContainerRef,
  ElementRef,
  OnDestroy,
  AfterViewChecked,
  HostBinding,
  ChangeDetectorRef,
} from "@angular/core";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { GLComponentAlertStatus } from "./golden-layout-component";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { ActiveWindowMonitor } from "shared/native-desktop/ActiveWindowMonitor";

@Component({
  selector: "golden-layout-popout",
  templateUrl: "./golden-layout-popout.html",
  animations: [],
  standalone: false,
})
export class GoldenLayoutPopout implements AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild("glPopout", { read: ElementRef, static: true })
  private embeddedComponentContainer!: ElementRef;

  @ViewChild("glPopoutContainer", { read: ViewContainerRef, static: true })
  popoutContainer!: ViewContainerRef;

  public resizeObservedElement: Element = null;

  public embeddedComponent: ComponentRef<any>;
  public embeddedComponentConfig: any = null;

  private afterViewInitResolve;
  private afterViewCheckedResolve;

  public showDevTools: boolean;

  public asMinimizedConfig: {
    top: number;
    left: number;
    width: number;
    height: number;
    state: string;
  };

  private _afterViewInitReady = new Promise<void>((res) => {
    this.afterViewInitResolve = res;
  });

  public get afterViewInitReady() {
    return Promise.resolve(this._afterViewInitReady);
  }

  public set afterViewInitReady(value) {
    this._afterViewInitReady = value;
  }

  private _afterViewCheckedReady = new Promise<void>((res) => {
    this.afterViewCheckedResolve = res;
  });

  public get afterViewCheckedReady() {
    return Promise.resolve(this._afterViewCheckedReady);
  }

  public set afterViewCheckedReady(value) {
    this._afterViewCheckedReady = value;
  }

  public position: any = { x: 0, y: 0, z: 0, w: 0, h: 0 };

  public animated: boolean = true;

  public callbackDock: any;
  public callbackClose: any;
  public callbackFocus: (
    popoutComponentId: string,
    popoutComponent: ComponentRef<any>,
    popout: GoldenLayoutPopout,
  ) => void;
  public updateLayoutCallback: any;

  public componentId: string;
  public frameTagTitle = null;
  private _title;

  public get title() {
    return this._title;
  }

  public set title(value) {
    this._title = value;
    if (document.body.dataset.frameTag) {
      this.frameTagTitle = `${document.body.dataset.frameTag} - ${this._title}`;
    } else {
      this.frameTagTitle = this._title;
    }
  }

  public show: boolean = false;
  public resizable: boolean = true;
  public minWidth: number = 0;
  public minHeight: number = 0;
  private refId: string = null; // for order entry
  public resizableDirections: Array<string> = null;

  public disabledClose: boolean = false;
  public disabledSettings: boolean = false;
  public disabledDebug: boolean = false;
  public disabledDock: boolean = false;
  public disabledRefresh: boolean = false;
  public disableAllActions: boolean = false;
  //this is just a temporary fix for entry window to close without dialogs
  public closeFromLogout: boolean = false;

  public showRefresh: boolean = false;
  public showDock: boolean = true;
  public showSetings: boolean = false;
  public showDebug: boolean = false;
  public badgeCount: number = 0;
  protected alertStatus: GLComponentAlertStatus;
  public alertStatusClass: string = ""; // do not modify directly use updateAlertStatus()

  public maximized: boolean = false;
  /** Is the window currently in focus? */
  public windowActive: boolean = false;

  private _nativeWindow: NativeDesktopWindow;
  public nativeDesktop: boolean = false;
  private activeWindowMonitor: ActiveWindowMonitor;

  /** Set lastFocused class on component when true */
  @HostBinding("class.lastFocused") lastFocused: boolean = false;

  public keyupListener: () => void;

  constructor(public layoutService: GoldenLayoutService) {
    this.nativeDesktop = NativeDesktopSupport.isNativeDesktopContainer();
  }

  ngAfterViewChecked(): void {
    this.afterViewCheckedResolve();
  }

  ngAfterViewInit() {
    this.afterViewInitResolve();
  }

  ngOnDestroy(): void {
    if (this.activeWindowMonitor) {
      // Remove all window listeners
      this.activeWindowMonitor.stop();
      this.activeWindowMonitor = null;
    }

    if (typeof this.keyupListener === "function") {
      this.keyupListener();
      this.keyupListener = null;
    }
    if (this.embeddedComponent != null) {
      this.embeddedComponent.destroy();
    }
  }

  public addContainerClass(cssClass: string) {
    this.afterViewInitReady.then(() => {
      let popoutContainerEl: Element = this.popoutContainer.element.nativeElement;

      if (popoutContainerEl) {
        popoutContainerEl.classList.add(cssClass);
      }
    });
  }

  public showSettings(event: Event): void {
    let activeCategory: string = "OrderEntry";
    this.layoutService.showSettings(activeCategory, event);
  }

  public toggleDebug(): void {
    if (this.refId != null) this.layoutService.triggerShowDebugEvent(this.refId);
  }

  public openDevTools(): void {
    let popoutContainerEl: Element = this.popoutContainer.element.nativeElement;
    let wnd = NativeDesktopSupport.getWindow(popoutContainerEl);
    wnd.openDevTools();
  }

  public setPosition(x: number, y: number, z: number, w: number, h: number) {
    this.position.x = x != null && x < 0 ? this.position.x : x;
    this.position.y = y != null && y < 0 ? this.position.y : y;
    this.position.z = z != null && z < 0 ? this.position.z : z;
    this.position.w = w != null && w < 0 ? this.position.w : w;
    this.position.h = h != null && h < 0 ? this.position.h : h;

    let timeout: number = w ? 0 : 500;
    setTimeout(this.fireResizeEvent.bind(this), 200); //timeout
    //this.fireResizeEvent.bind(this)
  }

  public minimize(): void {
    NativeDesktopSupport.getWindowConfig(this._nativeWindow).then((config) => {
      logger.out.info(
        `${this.constructor.name}.minimize() storing state top:${config.top} left:${
          config.left
        } width:${config.width} height:${config.height} state:${config.state}`,
      );

      this.asMinimizedConfig = config;

      this._nativeWindow.minimize();
    });
  }

  public maximize(): void {
    logger.out.info("Maximise button pressed");
    if (this.maximized) {
      this._nativeWindow.restore(true).then(
        () => logger.out.info(`Window restored successfully`),
        (reason) => logger.out.error(`Error restoring window: ${reason}`),
      );
    } else {
      this._nativeWindow.maximize(true).then(
        () => logger.out.info(`Window maximised successfully`),
        (reason) => logger.out.error(`Error maximising window: ${reason}`),
      );
    }
    this.maximized = !this.maximized;
  }

  public embedComponent(
    embeddedComponent: ComponentRef<any>,
    config: any,
    dockCallback?: any,
    closeCallback?: any,
    callbackFocus?: (
      popoutComponentId: string,
      popoutComponent: ComponentRef<any>,
      popout: GoldenLayoutPopout,
    ) => void,
    updateLayoutCallback?: any,
  ): Promise<void> {
    return this.afterViewInitReady.then(() => {
      let nativeElement = embeddedComponent.location.nativeElement;
      nativeElement.style.width = "100%";
      nativeElement.style.height = "100%";
      nativeElement.style.flexGrow = "1";
      nativeElement.removeAttribute("class");

      this.embeddedComponent = embeddedComponent;
      this.embeddedComponentContainer.nativeElement.appendChild(nativeElement);
      this.embeddedComponentConfig = config;

      if (this.embeddedComponent.instance["setParentPopup"])
        this.embeddedComponent.instance["setParentPopup"](this);

      if (!this.showDock) {
        // Override config that popout is not restorable on new session if setShowDock has been disabled.
        // This prevents certain utility windows that require state e.g. Detail Fill from being recreated with no data
        config.canRestore = false;
      }

      if (config.minimumSize && config.minimumSize.width >= 0 && config.minimumSize.height >= 0) {
        this.minWidth = config.minimumSize.width;
        this.minHeight = config.minimumSize.height;
      }

      this.callbackDock = dockCallback;
      this.callbackClose = closeCallback;
      this.callbackFocus = callbackFocus;
      this.updateLayoutCallback = updateLayoutCallback;

      if (this.embeddedComponent.instance["componentDeactivated"]) {
        // Ensure component is deactivated initially so it returns correct alert state
        this.embeddedComponent.instance["componentDeactivated"]();
      }
      if (this.embeddedComponent.instance["setLayoutUpdatedCallback"]) {
        this.embeddedComponent.instance["setLayoutUpdatedCallback"](this.onUpdateLayout.bind(this));
      }
    });
  }

  public getEmbeddedComponent(): ComponentRef<any> {
    return this.embeddedComponent;
  }

  public getEmbeddedComponentConfig(): any {
    return this.embeddedComponentConfig;
  }

  public hasContainerClass(cssClass: string): boolean {
    let popoutContainerEl: Element = this.popoutContainer.element.nativeElement;

    if (popoutContainerEl) {
      return popoutContainerEl.classList.contains(cssClass);
    }

    return false;
  }

  public removeContainerClass(cssClass: string) {
    let popoutContainerEl: Element = this.popoutContainer.element.nativeElement;

    if (popoutContainerEl) {
      popoutContainerEl.classList.remove(cssClass);
    }
  }

  public requestFocus(event?: Event) {
    if (this.disableAllActions) {
      return false;
    }

    if (this.callbackFocus) {
      this.afterViewCheckedReady.then(() => {
        this.callbackFocus(this.componentId, this.embeddedComponent, this);
      });
    }
  }

  public dock(): void {
    if (this.callbackDock) {
      this.afterViewCheckedReady.then(() => {
        this.callbackDock(this.componentId);
      });
    }
  }

  public close(): boolean {
    if (this.callbackClose) {
      this.afterViewCheckedReady.then(() => {
        return this.callbackClose(this.componentId);
      });
    }

    return true;
  }

  public onUpdateLayout(): void {
    if (this.updateLayoutCallback) {
      this.afterViewCheckedReady.then(() => {
        this.updateLayoutCallback(this.componentId);
      });
    }
  }

  public onResize(event: any): void {
    // send the event to the component that has resize method, and after view init is complete (var 'viewInitComplete' in component is true)
    if (
      this.embeddedComponent != null &&
      typeof this.embeddedComponent.instance["resize"] === "function" &&
      this.embeddedComponent.instance["viewInitComplete"]
    ) {
      this.embeddedComponent.instance["resize"](event);
    }
  }

  protected fireResizeEvent() {
    let element: any = this.popoutContainer.element.nativeElement;
    let resizeEvent: any = {
      position: { top: element.offsetTop, left: element.offsetLeft },
      size: { width: element.offsetWidth, height: element.offsetHeight },
    };
    this.onResize(resizeEvent);
  }

  public setShowRefresh(showRefresh: boolean) {
    this.showRefresh = showRefresh;
  }

  public setShowDock(showDock: boolean) {
    this.showDock = showDock;

    if (this._nativeWindow != null) {
      this._nativeWindow.updateOptions({
        maximizable: true,
        minimizable: true,
      });
    }
  }

  public get nativeWindow(): NativeDesktopWindow {
    return this._nativeWindow;
  }

  public set nativeWindow(nativeWindow: NativeDesktopWindow) {
    this._nativeWindow = nativeWindow;

    if (this.activeWindowMonitor) {
      this.activeWindowMonitor.stop();
    }
    this.activeWindowMonitor = new ActiveWindowMonitor(nativeWindow);
    this.windowActive = this.activeWindowMonitor.isActive;
    this.activeWindowMonitor.start((active) => {
      if (active) {
        // logger.out.info("Window is focused");
        this.windowActive = true;
        if (this.embeddedComponent?.instance?.["componentActivated"]) {
          this.embeddedComponent.instance["componentActivated"]();
        }
      } else {
        // logger.out.info("Window is blurred");
        this.windowActive = false;
        this.updateAlertStatus(this.alertStatus);
        if (this.embeddedComponent?.instance?.["componentDeactivated"]) {
          this.embeddedComponent.instance["componentDeactivated"]();
        }
      }
    });

    this.showDevTools = nativeWindow != null && NativeDesktopSupport.devMode;

    if (this.showDock) {
      this._nativeWindow.updateOptions({
        maximizable: true,
        minimizable: true,
      });
      this._nativeWindow.addEventListener("maximized", () => {
        // logger.out.info("Window is maximized");
        this.maximized = true;
      });
      this._nativeWindow.addEventListener("restored", () => {
        // logger.out.info("Window is restored");
        this.maximized = false;
      });
    }
  }

  public setShowSetings(showSetings: boolean) {
    this.showSetings = showSetings;
  }
  public setShowDebug(value: boolean) {
    this.showDebug = value;
  }

  public setRootRefId(_refId: string) {
    this.refId = _refId;
  }

  public toggleContainerClass(cssClass: string) {
    let popoutContainerEl: Element = this.popoutContainer.element.nativeElement;

    if (popoutContainerEl) {
      popoutContainerEl.classList.toggle(cssClass);
    }
  }

  public reload() {
    if (this.embeddedComponent.instance["reload"]) this.embeddedComponent.instance["reload"]();
  }

  public updateAlertStatus(alertStatus: GLComponentAlertStatus): void {
    if (alertStatus in GLComponentAlertStatus) {
      this.alertStatus = alertStatus;
      // logger.out.info(
      //   `glp - updating alertStatus to ${GLComponentAlertStatus[alertStatus]}, windowActive = ${this.windowActive}`,
      // );
      switch (alertStatus) {
        case GLComponentAlertStatus.NONE:
          this.alertStatusClass = "none";
          break;
        case GLComponentAlertStatus.ALERT:
        case GLComponentAlertStatus.ALERT_FLASH:
          this.alertStatusClass = "alert";
          break;
        case GLComponentAlertStatus.HCO:
        case GLComponentAlertStatus.HCO_FLASH:
          this.alertStatusClass = "alert_hco";
          break;
        case GLComponentAlertStatus.SCO:
        case GLComponentAlertStatus.SCO_FLASH:
          this.alertStatusClass = "alert_sco";
          break;
      }
    }
  }

  public bringToFront(): void {
    if (this.embeddedComponentContainer != null) {
      if (NativeDesktopSupport.isNativeDesktopContainer()) {
        try {
          let wnd = NativeDesktopSupport.getWindow(this.embeddedComponentContainer.nativeElement);
          wnd.setAsForeground(false);
        } catch (e) {
          logger.out.warn(
            `${this.constructor.name}: Error while attempting to bring ` +
              ` window to focus ${this._title}: ${e}`,
          );
        }
      } else {
        if (this.embeddedComponentConfig) {
          this.layoutService.bringPopoutFocus(
            this.embeddedComponentConfig.popoutComponentId,
            this.embeddedComponentConfig.id,
          );
        }
      }
    }
  }
}
